import { gql, useQuery } from "@apollo/client";
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import ContentTemplatePreview from "components/legacy/ContentTemplatePreview";
import {
  GetContentTemplateForBulkStageEdit,
  GetContentTemplateForBulkStageEditVariables,
  GetEmailTemplateContent,
  GetEmailTemplateContentVariables,
} from "generated/schemaTypes";
import _ from "lodash";
import { ResourceCustomTheme } from "styles/config";

import { BulkStageEditMode, StageEditsStateType } from "./types";

const GET_CONTENT_TEMPLATE = gql`
  query GetContentTemplateForBulkStageEdit($id: ID!) {
    currentOrganization {
      id
      contentTemplate: contentTemplateById(id: $id) {
        id
        name
        modules {
          id
          type
        }
      }
    }
  }
`;

const GET_EMAIL_TEMPLATE_CONTENT = gql`
  query GetEmailTemplateContent($id: ID!) {
    currentOrganization {
      id
      emailTemplateById(id: $id) {
        id
        name
        subject
        body
      }
    }
  }
`;

const useStyles = makeStyles((theme: ResourceCustomTheme) => ({
  root: {
    textAlign: "center",
  },
  table: {
    width: 600,
    margin: `${theme.spacing(3)}px auto`,
  },
  row: {
    borderBottom: 0,
  },
  templateName: {
    display: "flex",
    alignItems: "center",
  },
  labelCell: {
    width: 260,
    textAlign: "right",
  },
  labelContent: {},
  previewIcon: {
    marginLeft: 4,
  },
  context: {
    width: "100%",
    textAlign: "center",
    maxWidth: 440,
    margin: "0px auto",
  },
  emphasize: {
    color: theme.colors.RoyalPurple(),
  },
  subtext: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.colors.MidnightBlue(300),
  },
  light: {
    color: theme.colors.MidnightBlue(200),
  },
}));

function EmailTemplatePreview({
  name,
  mode,
}: {
  name: string;
  mode: BulkStageEditMode;
}) {
  const classes = useStyles();

  return (
    <TableRow className={classes.row}>
      <TableCell className={classes.labelCell}>
        {mode === BulkStageEditMode.Edit
          ? "Editing Email Template to"
          : "Email Template"}
        :
      </TableCell>
      <TableCell className={classes.labelContent}>
        <Typography variant="body1">
          <strong>{name}</strong>&nbsp;Template
        </Typography>
      </TableCell>
    </TableRow>
  );
}

function EmailActionMetadataPreview({
  cc,
  bcc,
  mode,
}: {
  cc: string[];
  bcc: string[];
  mode: BulkStageEditMode;
}) {
  const classes = useStyles();

  return (
    <TableRow className={classes.row}>
      <TableCell className={classes.labelCell}>
        {mode === BulkStageEditMode.Edit
          ? "Editing Email CC/BCC to"
          : "Email CC/BCC"}
        :
      </TableCell>
      <TableCell className={classes.labelContent}>
        {!!cc.length && (
          <Typography variant="subtitle2">CC: {_.join(cc, ", ")}</Typography>
        )}
        {!!bcc.length && (
          <Typography variant="subtitle2">BCC: {_.join(bcc, ", ")}</Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

function BulkContentTemplatePreview({
  name,
  modules,
  mode,
}: {
  name: string;
  modules: {
    id: string;
    type: string;
  }[];
  mode: BulkStageEditMode;
}) {
  const classes = useStyles();
  return (
    <TableRow className={classes.row}>
      <TableCell className={classes.labelCell}>
        {mode === BulkStageEditMode.Edit
          ? "Editing Content Template to"
          : "Content Template"}
        :
      </TableCell>
      <TableCell className={classes.labelContent}>
        <Tooltip title={<ContentTemplatePreview modules={modules} />}>
          <Typography variant="body1" className={classes.templateName}>
            <strong>{name}</strong>&nbsp;Template
          </Typography>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function ConfirmStageEdits({
  data,
  mode,
}: {
  data: StageEditsStateType;
  mode: BulkStageEditMode;
}) {
  const classes = useStyles();

  const contentTemplateId = data.contentTemplate?.id;
  const { data: contentTemplateData } = useQuery<
    GetContentTemplateForBulkStageEdit,
    GetContentTemplateForBulkStageEditVariables
  >(GET_CONTENT_TEMPLATE, {
    variables: { id: contentTemplateId as string },
    skip: !contentTemplateId,
  });

  const contentTemplate =
    contentTemplateData?.currentOrganization?.contentTemplate;
  const contentTemplateName = contentTemplate?.name;

  const emailTemplateId = data.emailTemplate?.id;
  const { data: emailTemplateData } = useQuery<
    GetEmailTemplateContent,
    GetEmailTemplateContentVariables
  >(GET_EMAIL_TEMPLATE_CONTENT, {
    // skip the query if undefined
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    variables: { id: emailTemplateId! },
    skip: !emailTemplateId,
  });

  // TODO: loading state
  const emailTemplateName =
    emailTemplateData?.currentOrganization?.emailTemplateById?.name;

  return (
    <div className={classes.root}>
      <Table className={classes.table}>
        <TableBody>
          {mode === BulkStageEditMode.Remove && (
            <TableRow className={classes.row}>
              <TableCell className={classes.labelCell}>Action:</TableCell>
              <TableCell className={classes.labelContent}>
                <Typography variant="body1">
                  <strong>Remove stages from candidate journeys</strong>
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {_.map(_.keys(data), (key) => {
            let rowComponent;
            switch (key) {
              case "contentTemplate":
                rowComponent = data.contentTemplate ? (
                  <BulkContentTemplatePreview
                    mode={mode}
                    name={contentTemplateName || ""}
                    modules={contentTemplate?.modules || []}
                  />
                ) : null;
                break;
              case "emailTemplate":
                rowComponent = data.emailTemplate ? (
                  <EmailTemplatePreview
                    mode={mode}
                    name={emailTemplateName || ""}
                  />
                ) : null;
                break;
              case "emailActionMetadata":
                rowComponent = data.emailActionMetadata ? (
                  <EmailActionMetadataPreview
                    mode={mode}
                    cc={data.emailActionMetadata.cc}
                    bcc={data.emailActionMetadata.bcc}
                  />
                ) : null;
                break;
              case "stageNameOverride":
                rowComponent = data.stageNameOverride ? (
                  <TableRow className={classes.row}>
                    <TableCell className={classes.labelCell}>
                      {mode === BulkStageEditMode.Edit
                        ? "Editing Stage display name to"
                        : "Stage display name"}
                      :
                    </TableCell>
                    <TableCell className={classes.labelContent}>
                      <Typography variant="body1">
                        <strong>{data.stageNameOverride.displayName}</strong>
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null;
                break;
              default:
                break;
            }

            return <div key={key}>{rowComponent}</div>;
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default ConfirmStageEdits;
